<template>
  <div class="d-flex flex-column ga-1">
    <template v-for="type in types" :key="type.type">
      <v-hover>
        <template v-slot:default="{ isHovering, props }">
          <button type="button" class="d-flex ga-2 pa-4 type align-center" v-bind="props" :disabled="isTypeDisabled(type)"
                  :class="{hover: isHovering && !isTypeDisabled(type)}" @click="openSettingsDialog(type)">
            <div class="icon-background d-flex">
              <v-icon :icon="type.icon" size="24" :color="isHovering && !isTypeDisabled(type) ? 'primary' : 'accent'"/>
            </div>
            <div>{{ $t(type.text) }}</div>
            <NewBadge v-if="type.type === 'tariffs'" class="new-badge ml-auto flex-shrink-0"/>
          </button>
        </template>
      </v-hover>
    </template>
  </div>
</template>

<script setup lang="ts">
import TextIcon from "~/assets/icons/chooser/text.vue";
import ButtonIcon from "~/assets/icons/chooser/button.vue";
import VideoIcon from "~/assets/icons/chooser/video.vue";
import ContactsIcon from "~/assets/icons/chooser/contacts.vue";
import FaqIcon from "~/assets/icons/chooser/faq.vue";
import TariffsIcon from "~/assets/icons/chooser/tariffs.vue";
import NewBadge from '~/assets/icons/new-badge.vue'
import PayIcon from "~/assets/icons/chooser/pay.vue";
import DelimiterIcon from "~/assets/icons/chooser/delimiter.vue";
import {computed, defineAsyncComponent, markRaw, ref} from "vue";
import {useDialogStore} from "@/stores/dialogStore";
import {useTapStore} from "@/stores/tapStore.js";

const emits = defineEmits(["chose"]);
const props = defineProps({
  blockId: { type: Number },
  componentUniqueId: { type: String },
  order: { type: Number }
});

const dialogStore = useDialogStore()
const tapStore = useTapStore()
const hasTariffs = computed(() => tapStore.currentTapLang?.blocks?.some(block =>
  block.blockItems?.some(item => item.type === 'tariffs')) ?? false)
const isTypeDisabled = (type: {type: string}) => type.type === 'tariffs' && hasTariffs.value

const types = ref([
  {
    text: 'Текстовый блок',
    icon: markRaw(TextIcon),
    type: 'text',
    component: markRaw(defineAsyncComponent(() => import('@/components/dialogs/block/TextSettings.vue')))
  },
  {
    text: 'Кнопка',
    icon: markRaw(ButtonIcon),
    type: 'button',
    component: markRaw(defineAsyncComponent(() => import('@/components/dialogs/block/ButtonSettings.vue')))
  },
  {
    text: 'Видео с YouTube',
    icon: markRaw(VideoIcon),
    type: 'video',
    component: markRaw(defineAsyncComponent(() => import('@/components/dialogs/block/VideoSettings.vue')))
  },
  {
    text: 'Оплата (Kaspi)',
    icon: markRaw(PayIcon),
    type: 'pay',
    component: markRaw(defineAsyncComponent(() => import('@/components/dialogs/block/BankSettings.vue')))
  },
  {
    text: 'Контакты',
    icon: markRaw(ContactsIcon),
    type: 'contacts',
    component: markRaw(defineAsyncComponent(() => import('@/components/dialogs/block/ContactsSettings.vue')))
  },
  {
    text: 'Вопрос и ответ',
    icon: markRaw(FaqIcon),
    type: 'faq',
    component: markRaw(defineAsyncComponent(() => import('@/components/dialogs/block/FAQSettings.vue')))
  },
  {
    text: 'Тарифы',
    icon: markRaw(TariffsIcon),
    type: 'tariffs',
    component: markRaw(defineAsyncComponent(() => import('@/components/dialogs/block/TariffsSettings.vue')))
  },
  {
    text: 'Разделитель',
    icon: markRaw(DelimiterIcon),
    type: 'delimiter'
  }
]);

const openSettingsDialog = (type) => {
  if (isTypeDisabled(type)) return
  emits('chose')
  if(type.component) {
    dialogStore.open(type.component, {
      blockId: props.blockId,
      componentUniqueId: props.componentUniqueId,
      order: props.order
    })
  } else {
    tapStore.saveComponent(props.blockId, props.order, { "type": type.type }, props.componentUniqueId)
    if(dialogStore.isOpen) {
      dialogStore.close()
    }
  }
}

</script>

<style scoped lang="sass">
.type
  width: 100%
  border: 0
  border-radius: 8px
  background: #F6F7F8
  color: #363636
  font: inherit
  font-size: 16px
  line-height: normal
  text-align: left
  cursor: pointer
  &:disabled
    cursor: default
    > *
      opacity: .35
    .new-badge
      opacity: .12
  .icon-background
    border-radius: 4px
    background: #DEE2E6
  &.hover
    color: #0D87EF
    background: #F8FCFF
    .icon-background
      background: #E7F3FD
</style>
